import React from 'react'
import { select } from 'd3-selection'
import { range, extent } from 'd3-array'
import { scaleQuantize, scaleLinear, scaleOrdinal } from 'd3-scale'
import { axisBottom, axisLeft } from 'd3-axis'
import { schemeBlues } from 'd3-scale-chromatic'
import ChartTooltip from '@ui/charts/ChartTooltip'
import css from './reportsCharts.module.scss'

const color = scaleQuantize().domain([0, 100]).range(schemeBlues[5])

const labels = [
  '12am',
  ...range(1, 12).map((n) => `${n}am`),
  '12pm',
  ...range(1, 12).map((n) => `${n}pm`),
]

let timeoutId

function legend({ svg, margin, width }) {
  const x = scaleLinear().domain(extent(color.domain())).rangeRound([0, 200])

  let g = svg.select('.legend')

  if (!g.node()) {
    g = svg.append('g').classed('legend', true)

    g.selectAll('rect')
      .data(color.range().map((d) => color.invertExtent(d)))
      .enter()
      .append('rect')
      .attr('height', 8)
      .attr('x', (d) => x(d[0]))
      .attr('width', (d) => x(d[1]) - x(d[0]))
      .attr('fill', (d) => color(d[0]))

    g.append('text')
      .attr('class', 'caption')
      .attr('x', x.range()[0])
      .attr('y', -6)
      .attr('fill', '#000')
      .attr('text-anchor', 'start')
      .attr('font-weight', '500')
      .text('Availability (%)')
      .style('font-family', 'var(--main-font-family)')

    const axis = g.call(
      axisBottom(x)
        .tickSize(13)
        .tickValues(
          color
            .range()
            .slice(1)
            .map((d) => color.invertExtent(d)[0])
        )
    )

    axis.select('.domain').remove()
    axis.selectAll('.tick > line').style('stroke', 'var(--border-color)')
    axis
      .selectAll('.tick > text')
      .style('fill', 'var(--grey1)')
      .style('font-size', '12px')
      .style('font-family', 'var(--main-font-family)')
  }

  g.attr('transform', `translate(${width - margin.right - 200}, 15)`)
}

function xaxis({ svg, margin, height, x }) {
  let g = svg.select('.xaxis')

  if (!g.node()) {
    g = svg
      .append('g')
      .classed('xaxis', true)
      .attr('transform', `translate(${margin.left}, ${height - margin.bottom})`)
  }

  const axis = g.call(
    axisBottom(x)
      .tickSize(3)
      .tickFormat((_, i) => (i % 4 === 0 ? labels[i] : ''))
  )

  axis.select('.domain').remove()
  axis.selectAll('.tick > line').style('stroke', 'var(--border-color)')
  axis
    .selectAll('.tick > text')
    .style('fill', 'var(--grey1)')
    .style('font-size', '12px')
    .style('font-family', 'var(--main-font-family)')
}

function yaxis({ svg, margin, y }) {
  let g = svg.select('.yaxis')

  if (!g.node()) {
    g = svg
      .append('g')
      .classed('yaxis', true)
      .attr('transform', `translate(${margin.left}, ${margin.top})`)
  }

  const axis = g.call(axisLeft(y))
  axis.select('.domain').remove()
  axis.selectAll('.tick > line').remove()
  axis
    .selectAll('.tick > text')
    .attr('dy', '1.5em')
    .style('fill', 'var(--grey1)')
    .style('font-size', '12px')
    .style('font-family', 'var(--main-font-family)')
}

function tiles({ svg, margin, data, x, y, rect, updateTooltip }) {
  let tilesContainer = svg.select('.tiles')

  if (!tilesContainer.node()) {
    tilesContainer = svg
      .append('g')
      .classed('tiles', true)
      .attr('transform', `translate(${margin.left}, ${margin.top})`)
      .style('font-family', 'var(--main-font-family)')
  }

  const tiles = tilesContainer.selectAll('.tile').data(data, (d) => d.id)

  tiles
    .enter()
    .append('rect')
    .classed('tile', true)
    .attr('x', (d) => x(d.hour))
    .attr('y', (d) => y(d.day))
    .attr('width', rect.w)
    .attr('height', rect.h)
    .attr('rx', 3)
    .attr('ry', 3)
    .style('stroke-width', 1)
    .style('stroke', 'white')
    .style('fill', color(0))
    .on('mouseenter', (d) => {
      clearTimeout(timeoutId)
      updateTooltip({
        x: x(d.hour) + margin.left + rect.w / 2,
        y: y(d.day) + margin.top - rect.h,
        d,
      })
    })
    .on('mouseleave', () => {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(() => updateTooltip({ d: null }), 100)
    })
    .transition()
    .duration(300)
    .style('fill', (d) => color(d.value))

  tiles
    .attr('x', (d) => x(d.hour))
    .attr('y', (d) => y(d.day))
    .attr('width', rect.w)
    .attr('height', rect.h)
    .on('mouseenter', (d) => {
      clearTimeout(timeoutId)
      updateTooltip({
        x: x(d.hour) + margin.left + rect.w / 2,
        y: y(d.day) + margin.top - rect.h,
        d,
      })
    })
    .transition()
    .duration(300)
    .style('fill', (d) => color(d.value))

  tiles.exit().transition().duration(300).style('opacity', 0).remove()
}

export default class Heatmap extends React.Component {
  svgRef = React.createRef()
  tooltipRef = React.createRef()
  state = { x: 0, y: 0, d: null }

  componentDidMount() {
    this.renderHeatmap()
  }

  componentDidUpdate() {
    this.renderHeatmap()
  }

  render() {
    const { width, height, title } = this.props
    const { x, y, d } = this.state
    return (
      <div style={{ position: 'relative', width }}>
        {title && (
          <div className={css.html_title}>
            <div className={css.html_title_inner}>{title}</div>
          </div>
        )}
        <svg width={width} height={height} ref={this.svgRef} />
        {d && (
          <div
            style={{
              opacity: 1,
              transform: `translate(${x}px, ${y}px)`,
              pointerEvents: 'none',
              position: 'absolute',
              top: 0,
              left: 0,
            }}
          >
            <ChartTooltip label={`${d.day} @${labels[d.hour]}`} value={`${d.value}%`} />
          </div>
        )}
      </div>
    )
  }

  renderHeatmap() {
    const { height, width, data } = this.props
    const margin = { top: 50, bottom: 30, left: 35, right: 10 }
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    const hours = range(0, 24)
    const svg = select(this.svgRef.current)
    const w = width - margin.left - margin.right
    const h = height - margin.top - margin.bottom
    const rect = { w: w / 24, h: h / 7 }

    const x = scaleOrdinal()
      .domain(hours)
      .range(range(0, w, w / 24))

    const y = scaleOrdinal()
      .domain(days)
      .range(range(0, h, h / 7))

    legend({ svg, margin, width })
    xaxis({ svg, margin, height, x })
    yaxis({ svg, margin, y })
    tiles({
      svg,
      margin,
      data,
      x,
      y,
      rect,
      updateTooltip: (params) => this.setState(params),
    })
  }
}
